/**
 * Enum de Tipo de Bolsa usado especificamente pelo módulo de Período de
 * Renovação de Matrícula. O GET devolve o label (string, ex.: "Interna") mas
 * o POST e os filtros exigem o valor inteiro do enum.
 *
 * ATENÇÃO: os valores abaixo são um placeholder (Interna = 1, Externa = 2).
 * Confirmar o mapeamento oficial junto do backend e ajustar apenas aqui —
 * todo o resto da feature consome `TipoBolsaEnum` / `TIPO_BOLSA_OPTIONS`.
 */
export enum TipoBolsaEnum {
  Interna = 1,
  Externa = 2,
}

export interface TipoBolsaOption {
  label: string;
  value: TipoBolsaEnum;
}

export const TIPO_BOLSA_OPTIONS: TipoBolsaOption[] = [
  { label: 'Interna', value: TipoBolsaEnum.Interna },
  { label: 'Externa', value: TipoBolsaEnum.Externa },
];

/** Converte o label devolvido pelo GET (ex.: "Interna") no valor numérico do enum. */
export function tipoBolsaLabelToValue(label: string | null | undefined): TipoBolsaEnum | null {
  const found = TIPO_BOLSA_OPTIONS.find(
    (o) => o.label.toLocaleLowerCase('pt') === (label ?? '').toLocaleLowerCase('pt')
  );
  return found ? found.value : null;
}

/** Item devolvido pelo GET /getAllAnoLectivo. */
export interface AnoLectivoOption {
  id: number;
  ano: string;
  activo: boolean;
}

/** Item devolvido pelo GET /PeriodoRenovacaoMatricula. */
export interface PeriodoRenovacaoMatricula {
  id: number;
  anoLectivoId: number;
  anoLectivo?: string;
  dataInicio: string;
  dataFim: string;
  /** Valor numérico do enum devolvido pela API. */
  tipoBolsaEnum: TipoBolsaEnum;
  /** Label textual devolvido pela API (ex.: "Interna"). */
  tipoBolsa?: string;
  /** Opcional: só é exibido na tabela se vier presente no payload. */
  activo?: boolean;
}

/** Body do POST /PeriodoRenovacaoMatricula. */
export interface PeriodoRenovacaoMatriculaCreateDto {
  anoLectivoId: number;
  /** yyyy-MM-dd */
  dataInicio: string;
  /** yyyy-MM-dd */
  dataFim: string;
  tipoBolsaEnum: TipoBolsaEnum;
}

/** Query params do GET /PeriodoRenovacaoMatricula. */
export interface PeriodoRenovacaoMatriculaListParams {
  PageNumber: number;
  PageSize: number;
  OrderBy?: string;
  AnoLectivoId?: number;
  DataInicio?: string;
  DataFim?: string;
  TipoBolsaEnum?: TipoBolsaEnum;
}

export interface ApiRetorno {
  codigo: number;
  mensagem: string;
}

/** Envelope padrão de paginação do backend. */
export interface PaginatedEnvelope<T> {
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  totalRecords: number;
  records: number;
  nextPage: string | null;
  previousPage: string | null;
  firstPage: string;
  lastPage: string;
  data: T[];
  retorno: ApiRetorno;
}

/** Envelope padrão de resposta simples (ex.: POST). */
export interface ApiEnvelope<T> {
  data: T;
  retorno: ApiRetorno;
}
